import { useState } from 'react';
import { IonButton, IonContent, IonPage } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useYbugApi } from 'ybug-react';
import { ybugSettings } from '../services/ybugSettings';
import { useProfileStore } from '../store/useProfileStore';
import { ProfileStatus } from '../types/profile';

/**
 * Terminal page for a profile the BE has walled off — `inactive`, `suspended`,
 * `removed` or `deleted`. Reached from the blocked-status check at the end of
 * `whoAmI()` via accountStatusService's navigator.
 *
 * The session is deliberately left intact (the user stays authenticated behind
 * the wall), so the status is read straight off the hydrated activeProfile.
 * One page, one message per status — the only action is Contact Support.
 *
 * Exempt from all app gates (RegistrationGate, ProtectedRoute) — see App.tsx.
 * No other navigation matters from here.
 */

/** The four blocked statuses, narrowed to the ones that have locale copy. */
type BlockedStatus = 'inactive' | 'suspended' | 'removed' | 'deleted';

const COPY_STATUSES: readonly BlockedStatus[] = [
  'inactive',
  'suspended',
  'removed',
  'deleted',
];

/**
 * Falls back to `inactive` copy for anything unexpected — a status the BE
 * added that this build doesn't know yet still gets a sane wall rather than
 * a raw i18n key.
 */
function resolveStatus(status: ProfileStatus | string | undefined): BlockedStatus {
  return COPY_STATUSES.includes(status as BlockedStatus)
    ? (status as BlockedStatus)
    : 'inactive';
}

export const AccountStatusPage: React.FC = () => {
  const { t } = useTranslation('common');
  const ybug = useYbugApi();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const [isOpeningSupport, setIsOpeningSupport] = useState(false);

  const status = resolveStatus(activeProfile?.status);

  // Only offer Contact Support when Ybug is actually wired (env flag on,
  // YbugProvider mounted) — same rule as GlobalErrorModal, so we never tell
  // the user to contact support with no way to do it.
  const canContactSupport = Boolean(ybug?.Ybug);

  const handleContactSupport = () => {
    if (!ybug?.Ybug) return;

    const feedbackBlock: Record<string, unknown> = {
      comment: `${t(`pages.accountStatus.supportReportComment.${status}`)}\n\n${t(
        'pages.accountStatus.supportAddDetailsPrompt',
      )}`,
      title: `Account status: ${status}`,
      feedback_type: 'bug',
    };
    if (activeProfile) {
      feedbackBlock.email = activeProfile.email;
      feedbackBlock.name = activeProfile.displayName;
    }

    const initPayload: Record<string, unknown> = {
      ...ybugSettings,
      feedback: feedbackBlock,
    };
    const language =
      activeProfile?.language ||
      (typeof navigator !== 'undefined' ? navigator.language : undefined);
    if (language) initPayload.language_override = language;
    if (activeProfile) {
      initPayload.user = {
        id: activeProfile.id,
        name: activeProfile.displayName,
        email: activeProfile.email,
        profileId: activeProfile.id,
        profileStatus: status,
      };
    }

    // ybug.init() REPLACES window.ybug_settings, so spread ybugSettings first
    // to keep the iOS flow directives (see ybugInit.tsx).
    ybug.init(initPayload as Parameters<typeof ybug.init>[0]);
    ybug.Ybug.open('feedback');

    // Ybug's iframe takes a few seconds on first open — keep a visible
    // affordance up rather than a dead button.
    setIsOpeningSupport(true);
    setTimeout(() => setIsOpeningSupport(false), 5000);
  };

  return (
    <IonPage>
      <IonContent>
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-xl font-bold text-foreground">
            {t(`pages.accountStatus.title.${status}`)}
          </h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            {t(`pages.accountStatus.body.${status}`)}
          </p>
          {canContactSupport && (
            <IonButton
              onClick={handleContactSupport}
              disabled={isOpeningSupport}
              className="mt-2"
            >
              {isOpeningSupport
                ? t('pages.accountStatus.openingSupport')
                : t('pages.accountStatus.supportCta')}
            </IonButton>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
